import './styles.scss';

import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router';
import { connect } from 'react-redux';

import { I18n } from 'react-redux-i18n';

import { getSlug, redirect } from 'Helpers/Uri';
import { getHighestResolution, getPhotoPageUrl } from 'Helpers/Photos';
import { getRouteByName } from 'Helpers/Routes';

import { isBrowser } from 'Helpers/Browser';
import { trackEvent, trackCheckout } from 'Helpers/Tracking';

import Head from 'Components/helpers/Head';
import Spinner from 'Components/layout/Spinner';
import Photo from 'Components/public/single-photo/Photo';
import RelatedPhotos from 'Components/public/single-photo/RelatedPhotos';
import AuthorPhotos from 'Components/public/single-photo/AuthorPhotos';
import Tags from 'Components/public/tags/RelatedList';
import NotFoundContainer from 'Containers/global/NotFound';

import { single, updatePhotos, setCurrentGalleryElement } from 'Redux/actions/photos';
import { addItem as cartAddItem } from 'Redux/actions/cart';
import { addItem as lightboxAddItem, removeItem as lightboxRemoveItem } from 'Redux/actions/lightbox';
import { reverseGeocode } from 'Redux/actions/gmap';
import { setError } from 'Redux/actions/global';

@connect(
    (state) => ({
        user: state.user,
        photos: state.photos,
        gmap: state.gmap,
        supports: state.supports
    }), { single, cartAddItem, lightboxAddItem, lightboxRemoveItem, updatePhotos, reverseGeocode, setCurrentGalleryElement, setError }
)
export default class SinglePhoto extends React.Component {
    static propTypes = {
        single: PropTypes.func.isRequired,
        cartAddItem: PropTypes.func.isRequired,
        lightboxAddItem: PropTypes.func.isRequired,
        lightboxRemoveItem: PropTypes.func.isRequired,
        updatePhotos: PropTypes.func.isRequired,
        setCurrentGalleryElement: PropTypes.func.isRequired,
        reverseGeocode: PropTypes.func.isRequired,
        setError: PropTypes.func.isRequired,
        params: PropTypes.shape({
            slug: PropTypes.string.isRequired,
            albumId: PropTypes.string,
            searchPhrase: PropTypes.string
        }).isRequired,
        photos: PropTypes.shape({
            photos: PropTypes.objectOf(PropTypes.object),
            currentGalleryElement: PropTypes.object,
            search: PropTypes.shape({
                photos: PropTypes.arrayOf(
                    PropTypes.object
                ).isRequired
            }).isRequired
        }).isRequired,
        user: PropTypes.shape({
            currentBusinessId: PropTypes.string,
            currentBrandId: PropTypes.string,
            profile: PropTypes.shape({
                admin: PropTypes.bool.isRequired,
                creditsCount: PropTypes.number,
                lightboxPhotos: PropTypes.array
            })
        }).isRequired,
        gmap: PropTypes.shape({
            isInitialized: PropTypes.bool.isRequired
        }).isRequired
    };
    static defaultProps = {};
    viewContentTracked = false;

    gmapCheckTimeout = null;
    gmapTryCount = 0;
    state = {
        photoAddress: null,
        galleryData: {
            isEnabled: false,
            nextPhoto: null,
            prevPhoto: null,
            referrer: null
        }
    };


    componentDidMount = () => {
        const slugParams = getSlug(this.props.params);
        this.viewContentTracked = false;

        require('Addons/gmap');
        window.scrollTo(0, 0);

        //If there's no id in url show notFound
        if (!slugParams.id) {
            return false;
        }

        //Set new photo
        this.getPhoto(this.props);
    }

    componentWillReceiveProps = nextProps => {
        const slugParams = getSlug(nextProps.params);
        if (
            (nextProps.user.currentBusinessId !== this.props.user.currentBusinessId) ||
            (nextProps.user.currentBrandId !== this.props.user.currentBrandId)
        ) {
            nextProps.single({ id: slugParams.id });
        }

        this.getPhoto(nextProps);
    }

    fetchAddressByLatLng = (lat, lng) => {
        if (!isBrowser()) {
            return false;
        }

        if (!this.props.gmap.isInitialized) {
            this.gmapCheckTimeout = setTimeout(() => {
                if (this.gmapTryCount < 5) {
                    this.gmapTryCount = this.gmapTryCount++;
                    return this.fetchAddressByLatLng(lat, lng);
                }

                return false;
            }, 300);
        } else {
            clearTimeout(this.gmapCheckTimeout);
            this.props.reverseGeocode(lat, lng)
                .then(address => {
                    this.setState({
                        ...this.state,
                        photoAddress: address
                    });
                })
                .catch(error => {
                });
        }
    }

    getPhoto = props => {
        const { photos } = props.photos;
        const slugParams = getSlug(props.params);

        //Check if photo is in store, if not download it
        if (typeof photos[slugParams.id] === 'undefined' || typeof photos[slugParams.id].single === 'undefined') {
            props.single({ id: slugParams.id });
        } else {
            const photoObject = photos[slugParams.id];

            if (!photoObject.single.photo) {
                return false;
            }

            if (!this.viewContentTracked) {
                trackEvent('viewContent', {
                    contentType: 'photo',
                    contentName: photoObject.single.photo.selectedCaption,
                    contentIds: [photoObject.single.photo.id]
                });
                this.viewContentTracked = true;
            }

            //Detect and set gallery data
            this.setGalleryData(props);

            //Fetch address from google maps api
            if (this.state.photoAddress === null) {
                if (photoObject.single.photo.exif && photoObject.single.photo.exif.latitude && photoObject.single.photo.exif.longitude) {
                    this.fetchAddressByLatLng(photoObject.single.photo.exif.latitude, photoObject.single.photo.exif.longitude);
                }
            }

            // //Check if user photos are in store
            // if (photoObject.single.photo.user && !photoObject.hasOwnProperty('user')) {
            //     props.userPhotos({
            //         photoId: photoObject.single.photo.id,
            //         userId: photoObject.single.photo.user.id
            //     });
            // }
        }
    }

    setGalleryData = props => {
        //If gallery mode is enabled
        let galleryLastPage = false;
        let galleryData = {
            isEnabled: false,
            query: '',
            page: 1,
            perPage: 40,
            filters: {},
            nextPhoto: null,
            prevPhoto: null,
            returnUrl: '/search/:query/:page:filters:hash'
        };
        const gallery = props.photos.currentGalleryElement;

        if (
            gallery &&
            Object.keys(gallery).length &&
            typeof gallery.query === 'string' &&
            typeof gallery.page === 'number' &&
            gallery.photo && Object.keys(gallery.photo).length &&
            Object.keys(gallery.elements).length
        ) {
            galleryData = {
                ...galleryData,
                ...gallery
            };

            let currentIndex = -1;
            gallery.elements.map((photo, index) => {
                if (photo.id === gallery.photo.id) {
                    galleryData.isEnabled = true;
                    currentIndex = index;
                }
            });

            let nextIndex = currentIndex + 1;
            let prevIndex = currentIndex - 1;
            if (typeof gallery.elements[nextIndex] !== 'undefined') {
                galleryData.nextPhoto = gallery.elements[nextIndex];
            }
            if (typeof gallery.elements[prevIndex] !== 'undefined') {
                galleryData.prevPhoto = gallery.elements[prevIndex];
            }
        }

        this.setState({
            ...this.state,
            galleryData: galleryData
        });

        return galleryData;
    }

    onNextPhoto = () => {
        const { nextPhoto, query, page } = this.state.galleryData;

        this.props.setCurrentGalleryElement({
            query: query,
            page: page,
            photo: nextPhoto
        });
        redirect(getPhotoPageUrl(nextPhoto));
    }

    onPrevPhoto = () => {
        const { prevPhoto, query, page } = this.state.galleryData;

        this.props.setCurrentGalleryElement({
            query: query,
            page: page,
            photo: prevPhoto
        });
        redirect(getPhotoPageUrl(prevPhoto));
    }

    onClose = () => {
        const { query, page, filters, returnUrl } = this.state.galleryData;
        const slugParams = getSlug(this.props.params);

        const photoObject = this.props.photos.photos[slugParams.id] || {};
        const { photo } = photoObject.single;

        const filtersQueryArray = [];
        Object.keys(filters).map(filter => {
            if (filters[filter]) {
                filtersQueryArray.push(`${filter}=${filters[filter]}`);
            }
        });

        let returnPath = returnUrl;
        returnPath = returnPath.replace('/:query', `/${query}`);
        returnPath = returnPath.replace('/:page', page && page > 1 ? '/page-' + page : '');
        returnPath = returnPath.replace(':filters', filtersQueryArray.length ? '?' + filtersQueryArray.join('&') : '');
        returnPath = returnPath.replace(':hash', `#photo-${photo.id}`);

        redirect(returnPath);
        this.resetGallery();
    }

    resetGallery = () => {
        this.props.setCurrentGalleryElement(null);
    }

    getMetaTags = photo => {
        let title = `${photo.selectedCaption} stock photo by ${photo.user ? photo.user.username : 'unkown'}`;
        let description = `${photo.selectedCaption} stock photo by ${photo.user ? photo.user.username : 'unkown'}. Download images and pictures from Foap's community of photographers.`;

        let latitude = null;
        let longitude = null;
        if (photo.exif) {
            latitude = photo.exif.latitude;
            longitude = photo.exif.longitude;
        }

        return {
            title: title,
            description: description,
            image: getHighestResolution(photo.resolutions),
            latitude: latitude,
            longitude: longitude
        };
    }

    onAddToCart = photo => {
        let albumId = null;
        if (typeof this.props.params.albumId !== 'undefined' && this.props.params.albumId) {
            albumId = this.props.params.albumId;
        }

        this.props.cartAddItem({ ...photo, albumId: albumId }, 'photo')
            .then(response => this.props.updatePhotos([{ id: photo.id, payload: { isInCart: true } }]));
    }
    onAddToLightbox = photo => {
        let albumId = null;
        if (typeof this.props.params.albumId !== 'undefined' && this.props.params.albumId) {
            albumId = this.props.params.albumId;
        }

        let photoWithAlbum = Object.assign({}, photo, { albumId: albumId });
        this.props.lightboxAddItem(photoWithAlbum, 'photo')
            .then(response => this.props.updatePhotos([{ id: photo.id, payload: { isInLightbox: true } }]));
    }

    onRemoveFromLightbox = photo => {
        this.props.lightboxRemoveItem(photo)
            .then(response => this.props.updatePhotos([{ id: photo.id, payload: { isInLightbox: false } }]));
    }

    render() {
        const { params } = this.props;
        const { profile } = this.props.user;
        const { galleryData, notFound } = this.state;

        const slugParams = getSlug(this.props.params);
        const photoObject = this.props.photos.photos[slugParams.id] || {};
        const { single, user, related } = photoObject;

        //If photo is not loaded yet
        if (!single || single.isPending || !single.isLoaded) {
            return (
                <div className="container-single-photo">
                    <Spinner className="page-spinner" />
                </div>
            );
        }

        if (!single.photo) {
            return (<NotFoundContainer {...this.props} />);
        }

        //If Photo is loaded
        const meta = this.getMetaTags(single.photo);
        return (
            <div className="container-single-photo">
                <Head
                    title={meta.title}
                    description={meta.description}
                    canonical={getRouteByName('singlePhoto', { slug: params.slug }).path}
                    image={meta.image}
                    robots={'noindex, nofollow'}
                    latitude={meta.latitude}
                    longitude={meta.latitude}
                >
                </Head>
                <Photo
                    user={this.props.user}
                    photoData={single.photo}
                    address={this.state.photoAddress}
                    creditsCount={profile ? profile.creditsCount : 0}

                    onAddToCart={() => this.onAddToCart(single.photo)}
                    onAddToLightbox={() => this.onAddToLightbox(single.photo)}
                    onRemoveFromLightbox={() => this.onRemoveFromLightbox(single.photo)}

                    onPrevPhoto={this.onPrevPhoto}
                    onNextPhoto={this.onNextPhoto}
                    onClose={this.onClose}
                    galleryData={galleryData}

                    isAdmin={this.props.user.profile ? this.props.user.profile.admin : false}
                    currentBrandId={this.props.user.currentBrandId}
                />
                <RelatedPhotos
                    photo={single.photo}
                    onEnterPhoto={this.resetGallery}
                />
                <AuthorPhotos
                    user={single.photo.user}
                    onEnterPhoto={this.resetGallery}
                />
                <div className="container-fluid">
                    <Tags
                        title={`${I18n.t('SINGLE_PHOTO.tags.listTitle')}`}
                        tags={single.photo.selectedTagLinks}
                    />
                </div>

            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/containers/global/SinglePhoto/index.js